/**
 * Time zone facts for the schedule surfaces, from the browser's own zone
 * data (`Intl`), which is also what `@date-fns/tz` and Luxon read. Pure so
 * it can be unit-tested; `scheduleSettings` owns the choice.
 */

/** The zone the device's clock runs in. */
export const deviceTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Every regional zone this browser offers, as IANA names in their
 * continent/city order. `UTC` is left out on every engine: V8 never lists
 * it and the others do, and the dialog seats it in its own row.
 */
export const listTimeZones = (): string[] =>
  Intl.supportedValuesOf('timeZone').filter((zone) => zone !== 'UTC');

/**
 * Whether `zone` is one the dialog can show as picked: `UTC` or a listed
 * regional zone. The one definition of a valid zone, shared by the store
 * that reads localStorage and the dialog that must find the pinned zone's
 * row. Aliases the browser can format in but does not list (`US/Pacific`,
 * `Etc/UTC`) are not valid: a hand-edited one degrades to the device's zone.
 */
export const isPickableTimeZone = (zone: string): boolean =>
  zone === 'UTC' || listTimeZones().includes(zone);

// One formatter per zone and style, kept: building one costs more than
// formatting with it, and a zone list labels four hundred of them on every
// keystroke. A formatter is immutable, so there is nothing to go stale.
type ZoneNameStyle = 'shortOffset' | 'long' | 'short';
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();
const zoneFormatter = (zone: string, style: ZoneNameStyle | 'clock'): Intl.DateTimeFormat => {
  const key = `${style}:${zone}`;
  let formatter = zoneFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(
      'en-US',
      style === 'clock'
        ? { timeZone: zone, hour: 'numeric', minute: '2-digit' }
        : { timeZone: zone, timeZoneName: style },
    );
    zoneFormatters.set(key, formatter);
  }
  return formatter;
};

const zoneNamePart = (zone: string, style: ZoneNameStyle, at: number): string => {
  const part = zoneFormatter(zone, style)
    .formatToParts(at)
    .find((part) => part.type === 'timeZoneName');
  if (!part) throw new Error(`no ${style} name for time zone ${zone}`);
  return part.value;
};

/** The zone's UTC offset at `at`, as the browser words it: `GMT-7`, `GMT+5:30`, `GMT`. */
export const timeZoneOffsetLabel = (zone: string, at: number): string =>
  zoneNamePart(zone, 'shortOffset', at);

/** The zone's name in words at `at`: `Pacific Daylight Time`; the offset again (`GMT-03:00`) where the browser has no name. */
export const timeZoneLongName = (zone: string, at: number): string =>
  zoneNamePart(zone, 'long', at);

/** The zone's abbreviation at `at` where English has one (`PDT`), else the browser's offset wording (`GMT+3`). */
export const timeZoneAbbreviation = (zone: string, at: number): string =>
  zoneNamePart(zone, 'short', at);

/** The wall clock in the zone at `at`: `2:41 AM`. */
export const timeZoneClock = (zone: string, at: number): string =>
  zoneFormatter(zone, 'clock').format(at);

/** The IANA name as prose: `America/Los Angeles`. */
const readableName = (zone: string): string => zone.replace(/_/g, ' ');

/** `America` of `America/Los_Angeles`, `America` of `America/Indiana/Knox`; `Other` for a name without a region (`UTC`). */
export const timeZoneRegion = (zone: string): string =>
  zone.includes('/') ? zone.slice(0, zone.indexOf('/')) : 'Other';

/** `Los Angeles` of `America/Los_Angeles`, `Knox` of `America/Indiana/Knox`; the whole name when it has no region (`UTC`). */
export const timeZoneCity = (zone: string): string => readableName(zone).split('/').pop() ?? zone;

/** `America/Los Angeles (GMT-7)`: the full name, for the compose hint and the picked value. */
export const timeZoneLabel = (zone: string, at: number): string =>
  `${readableName(zone)} (${timeZoneOffsetLabel(zone, at)})`;

/** `Los Angeles (GMT-7)`: the city alone, for a button with little room. */
export const timeZoneShortLabel = (zone: string, at: number): string =>
  `${timeZoneCity(zone)} (${timeZoneOffsetLabel(zone, at)})`;

/** `Pacific Daylight Time · GMT-7 · 2:41 AM now`: the line under a city in the zone list. */
export const timeZoneDetail = (zone: string, at: number): string =>
  `${timeZoneLongName(zone, at)} · ${timeZoneOffsetLabel(zone, at)} · ${timeZoneClock(zone, at)} now`;

/**
 * Everything a search for the zone may be typed as: the IANA name with and
 * without underscores, the long name and the abbreviation as of `at`. So
 * "los angeles", "pacific" and "PDT" all find `America/Los_Angeles`.
 */
export const timeZoneSearchText = (zone: string, at: number): string =>
  `${zone} ${readableName(zone)} ${timeZoneLongName(zone, at)} ${timeZoneAbbreviation(zone, at)}`;
